import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '../../design/Icon';
import { fmt } from '../../design/format';
import { Page, useShellStatus, useTerms } from '../../layout';
import { useHierarchyFilter } from '../../hooks/useHierarchyFilter';
import { ClassificationTag, SentimentBadge, SeverityLabel, SyntheticDataBadge } from '../../components/shared/Badges';
import { Delta, Kpi, KpiLedger } from '../../components/shared/Card';
import { Segmented } from '../../components/shared/Button';
import { DivergingRows } from '../../components/shared/charts/SmallCharts';
import { seqColor } from '../../components/shared/charts/core';
import { useToast } from '../../components/shared/Overlay';
import { EmptyState, ErrorState, Skeleton } from '../../components/shared/States';
import { insightsApi } from '../../services/insightsApi';
import { priorGap, useAnalytics } from '../../services/analyticsApi';
import { CALLS_STORAGE_KEY, EMPTY_CALL_FILTERS, displaySentiment, writeStored } from '../calls/callFilters';
import { fmtTime, relativeDay } from '../calls/callFormat';
import { emergencyCaption, matrixColumns, perThousandHomes, periodPhrase, pivotConcerns, sentimentShares, sentimentWindow, shortCategoryList, weekLabel } from './insightsModel';
import './insights.css';

const SENTIMENT_KEYS = [
  { name: 'Negative', color: 'var(--div-neg-2)' },
  { name: 'Mixed', color: 'var(--div-neg-1)' },
  { name: 'Neutral', color: 'var(--div-mid)' },
  { name: 'Positive', color: 'var(--div-pos-2)' },
];
const REPEAT_WINDOW_DAYS = 30;
const REPEAT_MIN_CALLS = 3;
const LOW_CONFIDENCE = 0.7;
const SHOWN_REPEAT_CALLERS = 5;

function loadInsights(key) {
  const { concerns: concernParams, ...filter } = JSON.parse(key);
  return Promise.all([
    insightsApi.overview(filter),
    insightsApi.concerns({ ...filter, groupBy: concernParams }),
    insightsApi.sentimentTrend({ ...filter, ...sentimentWindow(filter.to), interval: 'week' }),
    insightsApi.repeatCallers({ ...filter, withinDays: REPEAT_WINDOW_DAYS, minCalls: REPEAT_MIN_CALLS }),
    insightsApi.escalations(filter),
  ]).then(([overview, concerns, sentiment, repeat, escalations]) => ({ overview, concerns, sentiment, repeat, escalations }));
}

function useInsights(params) {
  const key = JSON.stringify(params);
  const { data, error, loading, retry } = useAnalytics(() => loadInsights(key), key);
  const status = error ? 'error' : loading ? 'loading' : 'ready';
  return [{ status, error, ...(data || {}) }, retry];
}

const DENSE_MATRIX_COLUMNS = 7;

/** Whether the element scrolls sideways, rechecked when it resizes or the deps change. */
function useHorizontalOverflow(...deps) {
  const ref = useRef(null);
  const [overflowing, setOverflowing] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const check = () => setOverflowing(el.scrollWidth > el.clientWidth + 1);
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return [ref, overflowing];
}

function ConcernsMatrix({ layout, cells, onOpen }) {
  const [norm, setNorm] = useState('count');
  const terms = useTerms();
  const { columns, rows } = pivotConcerns(cells, layout.columns);
  const shown = rows.map((r) => columns.map((c) => (norm === 'per' ? perThousandHomes(r.values[c.id] || 0, c.homes) : r.values[c.id] || 0)));
  const max = Math.max(1e-9, ...shown.flat());
  const format = norm === 'per' ? (v) => v.toFixed(1) : fmt.int;
  const dense = columns.length > DENSE_MATRIX_COLUMNS;
  const [wrapRef, overflowing] = useHorizontalOverflow(columns.length, norm);
  return (
    <section className="card c-9" data-testid="insights-by-community">
      <div className="card-head">
        <div>
          <div className="t" id="mx-title">
            {layout.title}
          </div>
          <div className="d" id="mx-sub">
            {norm === 'per' ? `Calls per 1,000 homes, so large and small ${terms.communitiesLower} compare fairly. ` : 'Analyzed calls carrying each classification. '}A call can carry several classifications.
          </div>
        </div>
        <div className="acts">
          <Segmented
            label="Measure"
            value={norm}
            onChange={setNorm}
            options={[
              { value: 'count', label: 'Calls' },
              { value: 'per', label: 'Per 1,000 homes' },
            ]}
          />
        </div>
      </div>
      <div className="card-body">
        <div className="table-wrap" ref={wrapRef}>
          <table className={dense ? 'mx mx-dense' : 'mx'} id="mx">
            <thead>
              <tr>
                <th>Classification</th>
                {columns.map((c) => (
                  <th key={c.id || 'unassigned'} title={c.name}>
                    {c.name}
                    <div className="caption" style={{ fontWeight: 400 }}>
                      {fmt.int(c.homes)} homes
                    </div>
                  </th>
                ))}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.categoryId}>
                  <td>
                    <ClassificationTag category={r.name} severity={r.severity} />
                  </td>
                  {columns.map((c, j) => {
                    const v = shown[i][j];
                    const color = seqColor(v / max, { contrast: true });
                    return (
                      <td key={c.id || 'unassigned'} style={{ background: color.fill, color: color.ink }} title={`${r.name} in ${c.name}: ${format(v)}`} onClick={() => onOpen(r.categoryId, c)}>
                        {format(v)}
                      </td>
                    );
                  })}
                  <td className="tot">{norm === 'per' ? '' : fmt.int(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="row caption mt-3" style={{ gap: 6 }}>
          Fewer
          <span id="mx-scale" className="row" style={{ gap: 2 }}>
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <span key={i} style={{ width: 20, height: 10, borderRadius: 2, background: `var(--seq-${i})`, display: 'inline-block', border: '1px solid var(--divider)' }} />
            ))}
          </span>
          More · Click a cell to open those calls
          {overflowing && ` · Scroll sideways for all ${columns.length} columns`}
        </div>
      </div>
    </section>
  );
}

function Rising({ topics, presetId }) {
  return (
    <section className="card c-3">
      <div className="card-head">
        <div>
          <div className="t">{presetId === 'last7' ? 'Rising this week' : 'Rising in this period'}</div>
          <div className="d">Compared with the previous 4-{presetId === 'last7' ? 'week' : 'period'} average</div>
        </div>
      </div>
      <div className="card-body" id="rising">
        {topics.length === 0 ? (
          <p className="caption">Nothing rose against the previous four periods.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} className="stack">
            {topics.map((t) => (
              <li key={`${t.categoryId}-${t.groupName}`}>
                <div className="row between">
                  <span className="small strong">{t.categoryName}</span>
                  <span className="delta up-bad">
                    <Icon name="arrowUp" className="ico-sm" />
                    {t.changePercent == null ? 'New' : `${Math.round(t.changePercent)}%`}
                  </span>
                </div>
                <div className="caption">
                  {[t.groupName, [t.topSubcategoryName, t.topCommunityName ? `mostly ${t.topCommunityName}` : null].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// A month of real calls can hold hundreds of review items (A018); the list grows a page at a time.
const ESCALATION_PAGE = 25;

function Escalations({ items, onReviewed }) {
  const [tab, setTab] = useState('needs');
  const terms = useTerms();
  const [reviewedNow, setReviewedNow] = useState({});
  const [fading, setFading] = useState({});
  const isReviewed = (e) => Boolean(e.reviewedAt || reviewedNow[e.callRecordId]);
  const needsCount = items.filter((e) => !isReviewed(e)).length;
  const [shown, setShown] = useState(ESCALATION_PAGE);
  const all = tab === 'needs' ? items.filter((e) => !isReviewed(e) || fading[e.callRecordId]) : items.filter((e) => isReviewed(e) && !fading[e.callRecordId]);
  const rows = all.slice(0, shown);
  return (
    <section className="card c-12" id="escalations">
      <div className="card-head">
        <div>
          <div className="t">Escalations, legal threats, and emergencies</div>
          <div className="d">Immediate and Within 48 Hours classifications, plus anything else the analysis sent for review by a person</div>
        </div>
        <div className="acts">
          <Segmented
            label="Review status"
            value={tab}
            onChange={(v) => {
              setTab(v);
              setFading({});
              setShown(ESCALATION_PAGE);
            }}
            options={[
              { value: 'needs', label: `Needs review ${needsCount}` },
              { value: 'reviewed', label: `Reviewed ${items.length - needsCount}` },
            ]}
          />
        </div>
      </div>
      <div className="card-body flush">
        <div className="table-wrap">
          <table className="table" id="esc">
            <thead>
              <tr>
                <th>Severity</th>
                <th style={{ width: 127 }}>Classification</th>
                <th>{terms.community}</th>
                <th>What the caller said</th>
                <th className="n">Confidence</th>
                <th>When</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="caption">
                    {tab === 'needs' ? 'Nothing waiting for review in this period.' : 'Nothing reviewed in this period yet.'}
                  </td>
                </tr>
              )}
              {rows.map((e) => (
                <tr key={e.callRecordId} style={fading[e.callRecordId] ? { opacity: 0.45 } : undefined}>
                  <td className="nowrap">
                    <SeverityLabel severity={e.severity} />
                  </td>
                  <td className="cell-title">{e.subcategoryName ? `${e.categoryName} › ${e.subcategoryName}` : e.categoryName}</td>
                  <td className="nowrap">
                    <div>{e.communityName || `No ${terms.communityLower}`}</div>
                    <div className="cell-sub">{e.hierarchyNodeName}</div>
                  </td>
                  <td className="small" style={{ minWidth: 280 }}>
                    {e.summaryShort || <span className="muted">No summary</span>}
                    <div className="cell-sub">{e.callerName || e.callerNumber || 'Unknown caller'}</div>
                  </td>
                  <td className="n">
                    {e.confidence == null ? (
                      '—'
                    ) : e.confidence < LOW_CONFIDENCE ? (
                      <span className="sev sev-48h">
                        <Icon name="alert" />
                        {Math.round(e.confidence * 100)}%
                      </span>
                    ) : (
                      `${Math.round(e.confidence * 100)}%`
                    )}
                  </td>
                  <td className="caption nowrap">
                    {relativeDay(e.startTime)} {fmtTime(e.startTime)}
                  </td>
                  <td className="nowrap">
                    <Link className="btn btn-sm" to={`/calls/${encodeURIComponent(e.callRecordId)}`}>
                      Open call
                    </Link>{' '}
                    {!isReviewed(e) && (
                      <button
                        className="btn btn-sm btn-ghost"
                        type="button"
                        onClick={async () => {
                          if (!(await onReviewed(e.callRecordId))) return;
                          setReviewedNow((r) => ({ ...r, [e.callRecordId]: true }));
                          if (tab === 'needs') setFading((f) => ({ ...f, [e.callRecordId]: true }));
                        }}
                      >
                        Mark reviewed
                      </button>
                    )}
                    {fading[e.callRecordId] && <span className="btn btn-sm btn-ghost">Reviewed</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {all.length > rows.length && (
          <div className="card-foot row" style={{ justifyContent: 'space-between' }}>
            <span className="caption">
              Showing {rows.length} of {all.length}, most severe and most recent first
            </span>
            <button className="btn btn-sm" type="button" onClick={() => setShown((n) => n + ESCALATION_PAGE)}>
              Show {Math.min(ESCALATION_PAGE, all.length - rows.length)} more
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/** Insights (mockup 14-insights.html): KPIs, concerns matrix, rising topics, weekly sentiment, repeat callers, review list. */
export function InsightsPage() {
  const h = useHierarchyFilter();
  const terms = useTerms();
  const { status: shellStatus } = useShellStatus();
  const navigate = useNavigate();
  const toast = useToast();
  const layout = useMemo(
    () => matrixColumns({ topNodes: h.topNodes, nodesById: h.nodesById, communitiesById: h.communitiesById, filter: h.filter, levels: h.levels, terms }),
    [h.topNodes, h.nodesById, h.communitiesById, h.filter, h.levels, terms]
  );
  const [state, retry] = useInsights({ ...h.params, concerns: layout.groupBy });

  const openCalls = (categoryId, column) => {
    writeStored(CALLS_STORAGE_KEY, { ...EMPTY_CALL_FILTERS, cls: [categoryId] });
    if (layout.groupBy === 'community') h.setCommunities(column.id ? [column.id] : []);
    else if (column.id) h.setDivision(column.id);
    navigate('/calls');
  };

  const markReviewed = async (id) => {
    try {
      await insightsApi.markReviewed(id);
      return true;
    } catch (error) {
      toast(error.response?.data?.message || "The call couldn't be marked reviewed", 'alert');
      return false;
    }
  };

  const page = (children) => (
    <Page
      title="Insights"
      subtitle="What callers needed and how they felt, from the transcripts of analyzed calls. Open a call to see the quote behind each value."
      actions={state.overview?.includesSyntheticData ? <SyntheticDataBadge /> : null}
      exportConfig={{ name: 'Insights', endpoint: '/export/datasets/Classifications', params: { title: 'Insights' }, extraFormats: [{ format: 'pdf', label: 'PDF summary', description: `${terms.community} concerns summary for the board (.pdf)` }] }}
    >
      {children}
    </Page>
  );

  if (state.status === 'error') return page(<ErrorState error={state.error} onRetry={retry} />);
  if (!state.overview)
    return page(
      <div className="stack-lg">
        <Skeleton height={96} />
        <Skeleton height={360} />
      </div>
    );

  const { overview, concerns, sentiment, repeat, escalations } = state;
  if (overview.analyzedCalls === 0)
    return page(
      <EmptyState
        icon="sparkle"
        title="No analyzed calls yet"
        body="Insights need transcripts. Calls are transcribed and analyzed 15 to 30 minutes after they end, and only when Teams recorded the call. Check AI and transcription settings if this persists."
      />
    );

  const answeredShare = overview.answeredCalls ? Math.round((overview.analyzedCalls / overview.answeredCalls) * 100) : 0;
  const negative = overview.negativeSentimentPercent;
  const previous = overview.previousNegativeSentimentPercent;
  const gap = priorGap({ from: h.range.from, to: h.range.to, historyStartDate: shellStatus?.callHistoryStartDate, hasPrior: negative != null && previous != null });
  const change = gap ? null : negative - previous;
  const spanishShare = overview.callsWithLanguage ? (overview.spanishCalls / overview.callsWithLanguage) * 100 : 0;

  return page(
    <div className="stack-lg">
      <KpiLedger loading={state.status === 'loading'}>
        <Kpi label="Analyzed calls" value={fmt.int(overview.analyzedCalls)} meta={`${answeredShare}% of answered calls`} />
        <Kpi label="Immediate" value={<span style={{ color: overview.immediateCalls ? 'var(--critical)' : undefined }}>{fmt.int(overview.immediateCalls)}</span>} meta={emergencyCaption(overview.immediateCalls, periodPhrase(h.range.id))} />
        <Kpi label="Within 48 Hours" value={fmt.int(overview.within48HoursCalls)} meta={shortCategoryList(overview.within48HoursTopCategories) || 'none'} />
        <Kpi
          label="Negative sentiment"
          value={negative == null ? '—' : fmt.pct(negative)}
          meta={
            gap ? (
              gap
            ) : (
              <>
                <Delta direction={change < 0 ? 'down-good' : change > 0 ? 'up-bad' : 'flat'}>{Math.abs(change).toFixed(1)} pts</Delta> vs prior
              </>
            )
          }
        />
        <Kpi label="Repeat callers" value={fmt.int(overview.repeatCallers)} meta={`${REPEAT_MIN_CALLS}+ calls in ${REPEAT_WINDOW_DAYS} days`} />
        <Kpi label="Spanish calls" value={fmt.pct(spanishShare)} meta={`${fmt.int(overview.spanishCalls)} ${overview.spanishCalls === 1 ? 'call' : 'calls'}`} />
      </KpiLedger>
      <div className="grid">
        <ConcernsMatrix layout={layout} cells={concerns} onOpen={openCalls} />
        <Rising topics={overview.rising} presetId={h.range.id} />
        <section className="card c-6">
          <div className="card-head">
            <div>
              <div className="t">Caller sentiment by week</div>
              <div className="d">Caller sentiment at the end of analyzed calls</div>
            </div>
          </div>
          <div className="card-body">
            <DivergingRows
              keys={SENTIMENT_KEYS}
              rows={sentiment.map((w) => {
                const values = sentimentShares(w.callerEndDistribution);
                return { label: weekLabel(w.periodStart), values, right: w.callCount ? `${values[0]}% neg.` : '—' };
              })}
            />
          </div>
        </section>
        <section className="card c-6">
          <div className="card-head">
            <div>
              <div className="t">Repeat callers</div>
              <div className="d">
                {REPEAT_MIN_CALLS} or more calls in {REPEAT_WINDOW_DAYS} days about the same classification
              </div>
            </div>
            <div className="acts">
              <span className="badge">
                {fmt.int(repeat.length)} {repeat.length === 1 ? 'caller' : 'callers'}
              </span>
            </div>
          </div>
          <div className="card-body flush">
            <div className="table-wrap">
              <table className="table compact" id="rep">
                <thead>
                  <tr>
                    <th>Caller</th>
                    <th className="n">Calls</th>
                    <th>About</th>
                    <th>Last call</th>
                  </tr>
                </thead>
                <tbody>
                  {repeat.length === 0 && (
                    <tr>
                      <td colSpan={4} className="caption">
                        No caller called {REPEAT_MIN_CALLS} or more times about the same thing.
                      </td>
                    </tr>
                  )}
                  {repeat.slice(0, SHOWN_REPEAT_CALLERS).map((r) => (
                    <tr key={r.callerNumber} className="clickable" tabIndex={0} onClick={() => navigate(`/calls/${encodeURIComponent(r.lastCallRecordId)}`)}>
                      <td className="nowrap">
                        <div className="cell-title">{r.callerName || r.callerNumber}</div>
                        <div className="cell-sub">{r.communityName}</div>
                      </td>
                      <td className="n">{r.callCount}</td>
                      <td>
                        <ClassificationTag category={r.categoryNames[0]} severity="Standard" />
                      </td>
                      <td className="nowrap">
                        <SentimentBadge sentiment={displaySentiment(r.lastCallerSentiment)} />
                        <div className="caption">{relativeDay(r.lastCallAt)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
        <Escalations items={escalations} onReviewed={markReviewed} />
      </div>
    </div>
  );
}
